import { motion, AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import * as Icons from 'lucide-react';
import { TEMPLATES, fillTemplate } from '../lib/templates.js';

export function TaskComposer({
  templateId,
  onTemplateChange,
  fieldValues,
  onFieldChange,
  task,
  onTaskChange,
  maxTokens,
  onMaxTokensChange,
}) {
  const tpl = useMemo(() => TEMPLATES.find((t) => t.id === templateId) || null, [templateId]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="panel p-6"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="section-title">
            <Icons.Wand2 className="h-4 w-4 text-accent-orange" />
            Task
          </div>
          <p className="mt-1 text-xs text-ink-dim">
            Pick a template or describe the work freeform. Fields below populate the prompt.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <span>max_tokens</span>
            <input
              type="range"
              min={128}
              max={4096}
              step={64}
              value={maxTokens}
              onChange={(e) => onMaxTokensChange(Number(e.target.value))}
              className="h-1 w-32 cursor-pointer appearance-none rounded-full bg-border-strong accent-accent-orange"
            />
            <span className="font-mono text-accent-orange-soft tabular-nums">{maxTokens}</span>
          </label>
        </div>
      </div>

      {/* Template gallery */}
      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
        <TemplatePill active={!templateId} onClick={() => onTemplateChange('')}>
          <Icons.PenTool className="h-3.5 w-3.5" />
          Freeform
        </TemplatePill>
        {TEMPLATES.map((t) => {
          const Icon = Icons[t.icon] || Icons.Sparkles;
          return (
            <TemplatePill key={t.id} active={templateId === t.id} onClick={() => onTemplateChange(t.id)}>
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </TemplatePill>
          );
        })}
      </div>

      {/* Template fields */}
      <AnimatePresence initial={false} mode="popLayout">
        {tpl?.fields?.map((f) => (
          <motion.div
            key={`${tpl.id}-${f.key}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="mb-3"
          >
            <label className="label mb-1.5">{f.label}</label>
            {f.textarea ? (
              <textarea
                value={fieldValues[f.key] || ''}
                onChange={(e) => onFieldChange(f.key, e.target.value)}
                placeholder={f.placeholder}
                rows={4}
                className={`input ${f.mono ? 'font-mono text-sm' : ''}`}
              />
            ) : (
              <input
                value={fieldValues[f.key] || ''}
                onChange={(e) => onFieldChange(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="input"
              />
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Final task preview */}
      <div className="mb-1 flex items-center justify-between">
        <label className="label">{tpl ? 'Resolved prompt (read-only)' : 'Task'}</label>
        {tpl && (
          <span className="font-mono text-[10px] text-ink-dim">
            ↳ {tpl.label}
          </span>
        )}
      </div>
      <textarea
        value={tpl ? fillTemplate(tpl, fieldValues) : task}
        onChange={(e) => !tpl && onTaskChange(e.target.value)}
        rows={tpl ? 5 : 6}
        readOnly={Boolean(tpl)}
        placeholder="Describe the task — be specific about what good looks like…"
        className={`input scrollbar-stealth ${tpl ? 'font-mono text-sm text-ink-muted' : ''}`}
      />
    </motion.section>
  );
}

function TemplatePill({ active, children, onClick }) {
  return (
    <motion.button
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={[
        'flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-all',
        active
          ? 'border-accent-orange/60 bg-accent-orange/15 text-accent-orange-soft shadow-glow-orange'
          : 'border-border-subtle bg-bg-surface/60 text-ink-muted hover:border-accent-cyan/40 hover:text-accent-cyan-light',
      ].join(' ')}
    >
      {children}
    </motion.button>
  );
}
